import React, { ReactNode } from 'react';

import IconButton from '@mui/material/IconButton/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { Button, Stack, useTheme } from '@mui/material';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import BeenhereRoundedIcon from '@mui/icons-material/BeenhereRounded';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

import useLanguage from 'src/hooks/Language/useLanguage';
import { SocialStatus } from 'src/utils/enums/socialMarketing';
import { BankTransactionStatus } from '../../../utils/enums/bank';

interface Props {
  id: number;
  type?: string;
  onView?: (id: number, type?: string) => void;
  onDuplicate?: (id: number, reason?: string) => void;
  onUpdateStatusSocialAccount?: (userId: number, status: SocialStatus) => void;
  onMarketingFeeActualMore?: () => void;
  onUpdateStatusMarketingFeeACtual?: (marketingFeeActualId: number, status: SocialStatus) => void;
  onViewMoreMarketingFeeActual?: (marketingFeeActualId: number) => void;
  onUpdateMarketingUsername?: (id: number) => void;
  onUpdateBankTransactionStatus?: (id: number, status: BankTransactionStatus) => void;
  children?: ReactNode;
}
const Action = ({
  id,
  onView,
  onDuplicate,
  onUpdateStatusSocialAccount,
  type,
  onUpdateStatusMarketingFeeACtual,
  onUpdateMarketingUsername,
  onViewMoreMarketingFeeActual,
  onUpdateBankTransactionStatus,
  children
}: Props) => {
  const {
    language: { common }
  } = useLanguage();

  const theme = useTheme();

  function handleView(event: React.MouseEvent) {
    event.stopPropagation();
    if (onView) {
      type ? onView(id, type) : onView(id);
    }
  }

  function handleDuplicate(event: React.MouseEvent) {
    event.stopPropagation();
    if (onDuplicate) {
      onDuplicate(id);
    }
  }

  const handleUpdateStatusSocialAccount = (event: React.MouseEvent, status: SocialStatus) => {
    event.stopPropagation();

    if (onUpdateStatusSocialAccount) {
      onUpdateStatusSocialAccount(id, status);
    }
  };

  function handleUpdateStatusMarketingFeeActual(event: React.MouseEvent, status: SocialStatus) {
    event.stopPropagation();
    onUpdateStatusMarketingFeeACtual && onUpdateStatusMarketingFeeACtual(id, status);
  }

  function handleViewMoreMarketingFeeActual(event: React.MouseEvent) {
    event.stopPropagation();
    onViewMoreMarketingFeeActual && onViewMoreMarketingFeeActual(id);
  }

  function handleUpdateMarketingUsername() {
    if (onUpdateMarketingUsername) {
      onUpdateMarketingUsername(id);
    }
  }

  const handleUpdateBankTransactionStatus = (event: React.MouseEvent, status: BankTransactionStatus) => {
    event.stopPropagation();

    if (onUpdateBankTransactionStatus) {
      onUpdateBankTransactionStatus(id, status);
    }
  };

  return (
    <div className="flex items-center justify-end">
      {children}
      {onView && (
        <Tooltip title={common.buttons.edit} arrow>
          <IconButton
            sx={{
              '&:hover': {
                background: theme.colors.primary.lighter
              },
              color: theme.palette.primary.main
            }}
            color="inherit"
            size="small"
            onClick={handleView}
          >
            <EditTwoToneIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {onDuplicate && (
        <Tooltip title={common.buttons.delete} arrow>
          <IconButton
            sx={{
              '&:hover': { background: theme.colors.error.lighter },
              color: theme.palette.error.main
            }}
            color="inherit"
            size="small"
            onClick={handleDuplicate}
          >
            <DeleteTwoToneIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {onUpdateStatusSocialAccount && (
        <>
          <Tooltip title={common.buttons.reject} arrow>
            <IconButton
              sx={{
                '&:hover': { background: theme.colors.error.lighter },
                color: theme.palette.error.main
              }}
              color="inherit"
              size="small"
              onClick={event => handleUpdateStatusSocialAccount(event, SocialStatus.Reject)}
            >
              <ClearOutlinedIcon fontSize="medium" />
            </IconButton>
          </Tooltip>

          <Tooltip title={common.buttons.approval} arrow>
            <IconButton
              sx={{
                '&:hover': { background: theme.colors.success.lighter },
                color: theme.palette.success.main
              }}
              color="inherit"
              size="small"
              onClick={event => handleUpdateStatusSocialAccount(event, SocialStatus.Approved)}
            >
              <BeenhereRoundedIcon fontSize="medium" />
            </IconButton>
          </Tooltip>
        </>
      )}

      {onViewMoreMarketingFeeActual && (
        <>
          <Tooltip title="Xem chi tiết" arrow>
            <IconButton
              sx={{
                '&:hover': {
                  background: theme.colors.primary.lighter
                },
                color: theme.palette.primary.main,
                marginRight: '10px'
              }}
              color="inherit"
              size="small"
              onClick={handleViewMoreMarketingFeeActual}
            >
              <MoreHorizIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      )}

      {onUpdateStatusMarketingFeeACtual && (
        <>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              color="error"
              onClick={event => handleUpdateStatusMarketingFeeActual(event, SocialStatus.Reject)}
            >
              Reject
            </Button>
            <Button
              variant="outlined"
              color="success"
              onClick={event => handleUpdateStatusMarketingFeeActual(event, SocialStatus.Approved)}
            >
              Approve
            </Button>
          </Stack>
        </>
      )}

      {onUpdateMarketingUsername && (
        <>
          <Tooltip title={common.buttons.edit} arrow>
            <IconButton
              sx={{
                '&:hover': {
                  background: theme.colors.primary.lighter
                },
                color: theme.palette.primary.main
              }}
              color="inherit"
              size="small"
              onClick={handleUpdateMarketingUsername}
            >
              <EditTwoToneIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      )}

      {onUpdateBankTransactionStatus && (
        <>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              color="error"
              onClick={event => handleUpdateBankTransactionStatus(event, BankTransactionStatus.reject)}
            >
              Reject
            </Button>
            <Button
              variant="outlined"
              color="success"
              onClick={event => handleUpdateBankTransactionStatus(event, BankTransactionStatus.cofirm)}
            >
              Confirm
            </Button>
          </Stack>
        </>
      )}
    </div>
  );
};

export default Action;
